/* Public 7-day post-ebook survey landing page.
 *
 * Loaded from the survey email link `/ebook-survey/:captureId`. No auth.
 * Single textarea + Submit. Hard-cap at 2000 chars (backend enforces).
 *
 * UX: optimistic — show the friendly thank-you state the instant POST
 * resolves successfully. Re-visiting the link after answering shows the
 * thank-you state right away so the user knows their answer landed.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EbookSurvey() {
  const { captureId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/ebook-survey/${captureId}`);
        if (cancelled) return;
        setFirstName(r.data.first_name || "");
        // If they already answered, jump straight to the thank-you state.
        if (r.data.already_answered) setSubmitted(true);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "We couldn't find your survey. The link may have expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [captureId]);

  const submit = async () => {
    if (answer.trim().length < 2) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/ebook-survey/${captureId}/respond`, { answer: answer.trim() });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center px-4 py-10" data-testid="ebook-survey-page">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 md:p-10">
        {loading ? (
          <div className="text-stone-500 text-sm">Loading your survey…</div>
        ) : error ? (
          <div className="text-center" data-testid="ebook-survey-error">
            <div className="text-rose-500 text-4xl mb-3">🔍</div>
            <h1 className="text-xl font-bold text-stone-900 mb-2">Survey not found</h1>
            <p className="text-stone-600 text-sm">{error}</p>
            <a href="/" className="inline-block mt-6 text-emerald-700 font-semibold hover:underline text-sm">← Back to iSpeedToClose</a>
          </div>
        ) : submitted ? (
          <div className="text-center" data-testid="ebook-survey-thanks">
            <div className="text-5xl mb-4">🙏</div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700 mb-2">Answer recorded</div>
            <h1 className="text-2xl font-extrabold text-stone-900 mb-3 leading-tight">
              Thanks{firstName ? `, ${firstName}` : ""} — we read every one.
            </h1>
            <p className="text-stone-600 text-sm leading-relaxed mb-6">
              Your answer goes straight to Tracy. The most-requested fix
              gets shipped next — check <a href="/changelog" className="text-emerald-700 font-semibold hover:underline">/changelog</a>
              {" "}to see what lands.
            </p>
            <a
              href="/v3"
              className="inline-block px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition"
              data-testid="ebook-survey-back-cta"
            >
              Back to the app  →
            </a>
          </div>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-700 mb-2">
              iSpeedToClose · Day 7 check-in
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight mb-2">
              Hey{firstName ? `, ${firstName}` : " there"} — one question.
            </h1>
            <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-6">
              Your week-one experience is the most valuable feedback we get.
              Pick the answer that hits hardest.
            </p>

            <div className="border-2 border-emerald-500 bg-emerald-50 rounded-xl p-4 md:p-5 mb-5">
              <div className="text-[11px] tracking-widest uppercase font-bold text-emerald-700 mb-1.5">
                ⚡ ONE QUESTION
              </div>
              <div className="text-stone-900 font-bold text-base md:text-lg leading-snug">
                What&apos;s the #1 thing you wanted to do but couldn&apos;t figure
                out in iSpeedToClose this week?
              </div>
            </div>

            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value.slice(0, 2000))}
              placeholder="Just type your honest answer here. One sentence is plenty — no novel required."
              rows={6}
              className="w-full rounded-xl border-2 border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 px-4 py-3 text-stone-900 text-sm md:text-base resize-none outline-none transition"
              data-testid="ebook-survey-answer-input"
              disabled={submitting}
            />
            <div className="flex items-center justify-between mt-3 mb-5">
              <span className="text-xs text-stone-400 tabular-nums">{answer.length} / 2000</span>
              <span className="text-xs text-stone-500">Goes straight to Tracy. No login.</span>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={submitting || answer.trim().length < 2}
              className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white font-bold text-base transition"
              data-testid="ebook-survey-submit"
            >
              {submitting ? "Sending…" : "Send answer  →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
